import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, AlertOctagon, Info, X } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/cn';

interface MaintenanceBannerData {
  enabled: boolean;
  message: string | null;
  returnAt: string | null;
  supportUrl: string | null;
  supportLabel: string | null;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
}

const REFRESH_INTERVAL_MS = 60_000;
const DISMISS_KEY_PREFIX = 'kairos.maintenance-banner-dismissed:';

/**
 * Story 18.7 — top-of-app banner that warns users about ongoing or
 * scheduled maintenance.
 *
 * Polls /api/v1/me/maintenance-banner every minute so that an admin
 * toggling the banner from another tab propagates without a hard
 * refresh. Dismissed banners stay dismissed until either the message
 * changes or the user logs out and back in.
 */
export function MaintenanceBanner() {
  const { t, i18n } = useTranslation();
  const tokens = useAuthStore((s) => s.tokens);
  const [banner, setBanner] = useState<MaintenanceBannerData | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!tokens) {
      setBanner(null);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const { data } = await apiClient.get<MaintenanceBannerData>(
          '/me/maintenance-banner',
        );
        if (cancelled) return;
        setBanner(data);
        const key = makeDismissKey(data);
        setDismissed(key !== null && localStorage.getItem(key) === '1');
      } catch {
        // ignore — banner is optional, never block the app on it
      }
    }
    load();
    const handle = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [tokens]);

  if (!banner || !banner.enabled || dismissed) return null;

  const severity = banner.severity;
  const Icon =
    severity === 'CRITICAL' ? AlertOctagon : severity === 'WARNING' ? AlertTriangle : Info;
  const message =
    banner.message?.trim() || t('maintenanceBanner.defaultMessage');
  const returnAt = banner.returnAt
    ? formatReturnAt(banner.returnAt, i18n.language)
    : null;

  function dismiss() {
    if (!banner) return;
    const key = makeDismissKey(banner);
    if (key) localStorage.setItem(key, '1');
    setDismissed(true);
  }

  return (
    <div
      role="alert"
      data-testid="maintenance-banner"
      className={cn(
        'flex flex-wrap items-center gap-3 border-b px-4 py-2 text-sm',
        severity === 'CRITICAL' &&
          'border-red-800 bg-red-950/60 text-red-100',
        severity === 'WARNING' &&
          'border-amber-800 bg-amber-950/60 text-amber-100',
        severity === 'INFO' &&
          'border-blue-800 bg-blue-950/60 text-blue-100',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">
        {message}
        {returnAt && (
          <>
            {' '}
            <span className="font-medium">
              {t('maintenanceBanner.expectedBack', { at: returnAt })}
            </span>
          </>
        )}
        {banner.supportUrl && (
          <>
            {' '}
            <a
              href={banner.supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:opacity-80"
            >
              {banner.supportLabel?.trim() || t('maintenanceBanner.contactSupport')}
            </a>
          </>
        )}
      </span>
      <button
        onClick={dismiss}
        aria-label={t('maintenanceBanner.dismiss')}
        className="rounded p-1 hover:bg-white/10"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function makeDismissKey(banner: MaintenanceBannerData): string | null {
  if (!banner.enabled) return null;
  const fingerprint = [
    banner.severity,
    banner.message ?? '',
    banner.returnAt ?? '',
  ].join('|');
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    hash = (hash * 31 + fingerprint.charCodeAt(i)) | 0;
  }
  return `${DISMISS_KEY_PREFIX}${hash}`;
}

function formatReturnAt(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}
